import { Theme, useTheme } from '@mui/material/styles';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { StringParam, useQueryParam } from 'use-query-params';
import SideDrawer from '../../components/layout/drawer/SideDrawer';
import Tab from '../../features/userAccount/Tab';
import TabContent from '../../features/userAccount/TabContent';
import useMediaQuery from '@mui/system/useMediaQuery/useMediaQuery';
import { useStore } from '../../store/store';
import { ECreatorUserRole } from '../../store/user/userStore';
import Typography from '@mui/material/Typography/Typography';
import { CircularProgress } from '@mui/material';
import ActiveIntegrations from '../../features/integrations/ActiveIntegrations';
import AddNewIntegration from '../../features/integrations/AddNewIntegration';
import { IWhoAmIResponse } from '../../services/models/UserProfileViewModel';
import TopBar from '../../components/layout/topbar/TopBar';

export default observer(function Integrations() {
  const { userStore } = useStore();
  const { role, getWhoAmI } = userStore;
  const theme = useTheme();

  const [tabParam, setTabParam] = useQueryParam('tab', StringParam);
  const [value, setValue] = useState(tabParam ? tabParam : 'Active Integrations');
  const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const [ownerID, setOwnerID] = useState<number | null>(null);
  const [loadingOwnerID, setLoadingOwnerID] = useState(false);

  const useStyles = createUseStyles((theme: Theme) => ({
    tab: {
      display: 'flex',
      flexDirection: 'column',
      '& > button': {
        position: 'relative',
        height: 50,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: 30,
        cursor: 'pointer',
      },
      '& :not(:last-of-type)': {
        borderBottom: `1px solid ${theme.palette.bgFour.main}`,
      },
    },
    tabIndicator: { background: theme.palette.primary.main, width: 4, height: '100%', position: 'absolute', left: 0, top: 0 },
  }));

  const classes = useStyles({ theme });

  useEffect(() => {
    setValue(tabParam ? tabParam : 'Active Integrations');
  }, [tabParam]);

  // Load owner ID on mount
  useEffect(() => {
    const loadOwnerID = async () => {
      if (!ownerID && !loadingOwnerID) {
        setLoadingOwnerID(true);
        try {
          const whoAmIResponse: IWhoAmIResponse = await getWhoAmI();
          setOwnerID(whoAmIResponse.ownerID || null);
        } catch (error) {
          console.error('Error loading owner ID:', error);
        } finally {
          setLoadingOwnerID(false);
        }
      }
    };
    loadOwnerID();
  }, [ownerID, loadingOwnerID, getWhoAmI]);

  const handleClick = (aValue: string) => {
    setTabParam(aValue);
  };

  // Show loading state until ownerID is available
  if (loadingOwnerID || !ownerID) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
          <Typography variant='h6' sx={{ color: theme.palette.text.primary }}>
            Loading integrations...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <>
      <TopBar />
      <div style={{ display: 'flex', flex: '1 1 auto', overflow: 'hidden' }}>
        <SideDrawer isOpen isLeft isPersistent>
          <div className={classes.tab}>
            <Tab title={'Active Integrations'} onClick={handleClick} value={value} />
            <Tab title={'Add New Integration'} onClick={handleClick} value={value} />

            {role >= ECreatorUserRole.superAdmin && <Tab title={'Settings'} onClick={handleClick} value={value} />}
          </div>
        </SideDrawer>
        <div style={{ overflow: 'auto', flex: '1 1 auto', padding: theme.spacing(fullScreen ? 4 : 10) }}>
          <TabContent title='Active Integrations' value={value} component={<ActiveIntegrations ownerID={ownerID!} loadingOwnerID={loadingOwnerID} />} />
          <TabContent title='Add New Integration' value={value} component={<AddNewIntegration ownerID={ownerID!} loadingOwnerID={loadingOwnerID} />} />
        </div>
      </div>
    </>
  );
});
